import { useCallback } from "react";
import { useShipToApi } from "./useShipToApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import { assertArrayFields } from "@utils/apiPayload";
import type {
    ProductFacetsResponse,
    ProductFacetsStatus,
} from "@models/productFacet";
import {
    productFacetsAtom,
    productFacetsStatusAtom,
} from "@/store/productFacetAtoms";

// GET /products/facets (#226, API PR 246). A failed read — 404 included — settles the status
// on `failed`, and the Brand/Vendor sections and the In-Stock toggle stay hidden.
const FACETS_ENDPOINT = "/products/facets";

// Which read is allowed to publish. Module-scope, because the atoms it writes are module-scope
// too (the app mounts no Jotai Provider) while every caller's request guard is per-mount: a
// remount mid-flight puts two reads in flight against ONE store, and only the newest may report.
//
// Deliberately NOT a per-caller "cancelled" flag, which is the shape this replaces. A flag says
// "my caller went away", which is not the same question — when nothing replaced the read, that
// answer suppressed the only write there was ever going to be and left the status stuck on
// "loading" forever (#226 review F1). A sequence answers "has someone else taken over?", so a
// suppressed write always has a successor that will publish in its place. Latest-only
// (`isLatest`).
const facetReads = createReadGuard();

export const useProductFacetApi = () => {
    const { callShipToApi } = useShipToApi();
    // Session-bound: a read answering after sign-out publishes nothing, and the
    // status stays at the "idle" endSessionAtom reset it to.
    const setFacets = useSessionSetAtom(productFacetsAtom);
    const setStatus = useSessionSetAtom(productFacetsStatusAtom);

    // Brand + vendor chip values (#226). skipErrorNotification: this is a SECONDARY read
    // that decorates a results page which renders fine without it — a failure costs the
    // shopper two chip lists, not their results, so it must not toast over a working grid.
    // The panel degrades to hiding those two sections (see FilterPanel).
    const getProductFacets =
        useCallback(async (): Promise<ProductFacetsResponse> => {
            const { isLatest } = facetReads.begin();

            // Stale-while-revalidate: a refresh holds the last good state instead of blanking it,
            // exactly as productFacetsAtom holds the previous payload. Dropping to "loading" here
            // would unmount and remount the In-Stock toggle on every return to a results page,
            // while Brand and Vendor — which keep their chips — sat still. Only a first load or a
            // retry after failure has nothing to hold, and both of those already withhold the
            // toggle, so that transition is invisible.
            setStatus((previous) =>
                previous === "loaded" ? "loaded" : "loading",
            );

            // The outcome is decided in the try and PUBLISHED in the finally — the apiHooks
            // folder's mandated shape (see useCategoryApi), adapted from a boolean to a
            // three-valued outcome. `finally` is what makes "loading" non-absorbing: there is no
            // exit from this function, thrown or returned, that does not run it. The pessimistic
            // default matters — anything that is not a completed read is a failure, and `failed`
            // has to stay distinct from "never asked" because the In-Stock toggle reads this to
            // decide whether the facets endpoint exists at all (see ProductFacetsStatus).
            let outcome: ProductFacetsStatus = "failed";

            try {
                const response = await callShipToApi<ProductFacetsResponse>(
                    FACETS_ENDPOINT,
                    { skipErrorNotification: true },
                );

                // A 2xx whose body is not the { brands, vendors } shape — an empty 200, an HTML
                // page from a proxy — is a failure, not facets: published, FilterPanel would
                // reach it with .map() in a render path (#399). The throw is what makes the
                // finally publish `failed` like any other failed read.
                assertArrayFields(FACETS_ENDPOINT, response, [
                    "brands",
                    "vendors",
                ]);

                if (isLatest()) setFacets(response);
                outcome = "loaded";

                return response;
            } finally {
                if (isLatest()) setStatus(outcome);
            }
        }, [callShipToApi, setFacets, setStatus]);

    return {
        getProductFacets,
    };
};
